/**
 * The 0-10 rating scale and its conversion to letter grades.
 *
 * Ratings are captured per KRA and roll up through two weighted levels: a goal's
 * rating is the weighted mean of its KRAs (`meanRating`), and the overall rating is
 * the weighted mean of the *organizational* goals (`weightedOverall`), since
 * individual goals carry 0 weightage by definition.
 *
 * The two levels differ on purpose. Goal weightage is a 100% budget across the set -
 * it is what the appraisal is actually divided by. KRA weightage is a relative split
 * inside one goal, opt-in, and falls back to a plain mean when it is left alone.
 */

export const RATING_MIN = 0;
export const RATING_MAX = 10;

export const RATING_DESCRIPTORS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 0, label: 'Poor' },
  { value: 2, label: 'Needs Improvement' },
  { value: 4, label: 'Average' },
  { value: 6, label: 'Good' },
  { value: 8, label: 'Excellent' },
  { value: 10, label: 'Outstanding' },
];

export const GRADES = ['A+', 'A', 'B+', 'B', 'C', 'D'] as const;
export type Grade = (typeof GRADES)[number];

/** Inclusive lower bounds, highest first. */
/**
 * Exported so the analytics distribution buckets people by the SAME bands their
 * appraisal grades them with. The two used to be separate lists, and 8.0 came out
 * "A - Excellent" on the appraisal but "Outstanding" on the HR dashboard, where
 * Outstanding is A+ and starts at 9. One vocabulary, one source.
 */
export const GRADE_BANDS: ReadonlyArray<{ min: number; grade: Grade; label: string }> = [
  { min: 9, grade: 'A+', label: 'Outstanding' },
  { min: 8, grade: 'A', label: 'Excellent' },
  { min: 7, grade: 'B+', label: 'Very Good' },
  { min: 6, grade: 'B', label: 'Good' },
  { min: 4, grade: 'C', label: 'Average' },
  { min: RATING_MIN, grade: 'D', label: 'Needs Improvement' },
];

/**
 * The five choices a KRA is rated with, at every stage.
 *
 * A KRA is answered by picking a band, never by typing a figure: 7 and 7.5 do not
 * describe two different performances anybody could defend, and self and manager
 * have to be choosing from the same list for the gap between them to mean anything.
 *
 * Each value sits exactly on its GRADE_BANDS floor, so one KRA grades as the band it
 * was picked from. `B+` is therefore only ever produced by a weighted average across
 * goals - which is the one place a half-band is a real result rather than a guess.
 */
export const KRA_RATING_OPTIONS: ReadonlyArray<{ value: number; grade: Grade; label: string }> = [
  { value: 10, grade: 'A+', label: 'Exceedingly Exceeds Expectations' },
  { value: 8, grade: 'A', label: 'Exceeds Expectations' },
  { value: 6, grade: 'B', label: 'Meets Expectations' },
  { value: 4, grade: 'C', label: 'Below Expectations' },
  { value: 2, grade: 'D', label: 'Exceedingly Below Expectations' },
];

export function isValidRating(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= RATING_MIN && value <= RATING_MAX;
}

export function assertValidRating(value: unknown): number {
  if (!isValidRating(value)) {
    throw new RangeError(`Rating must be between ${RATING_MIN} and ${RATING_MAX}, received ${String(value)}`);
  }
  return value;
}

export function toGrade(rating: number): Grade {
  const band = GRADE_BANDS.find((b) => rating >= b.min);
  // The last band starts at RATING_MIN, so this only trips on NaN or negatives.
  return band?.grade ?? 'D';
}

export function gradeLabel(grade: Grade): string {
  return GRADE_BANDS.find((b) => b.grade === grade)?.label ?? 'Not rated';
}

/** The nearest descriptor word for a numeric rating, e.g. 7.4 -> "Good". */
export function describeRating(rating: number): string {
  return RATING_DESCRIPTORS.reduce((best, d) =>
    Math.abs(d.value - rating) < Math.abs(best.value - rating) ? d : best,
  ).label;
}

/** Two decimals - enough to break ties in calibration, not enough to imply false precision. */
export function roundRating(value: number): number {
  return Math.round(value * 100) / 100;
}

export interface WeightedKraRating {
  weightage: number;
  rating: number;
}

/**
 * A goal's rating from the KRAs under it - the second level of weightage.
 *
 * Weighted by `Kra.weightage` when the goal's KRAs carry any, and a plain mean when
 * every one of them is 0. That fallback is the design and not a safety net: the second
 * level is opt-in, so a simple role never forces HR through the exact-100 arithmetic a
 * second time, and a goal whose KRAs are all 0 rates exactly as it did before the
 * column existed.
 *
 * Deliberately NOT normalised to 100 and not validated here. These are relative shares
 * inside one goal - 60/40 and 6/4 are the same split - because the 100% budget that
 * actually decides the appraisal is spent one level up, on `Goal.weightage`. Making a
 * KRA's KPIs total 100 as well would be a second exact-100 rule to satisfy per goal for
 * no arithmetic difference.
 *
 * A partial split still weights: 60/40/0 leaves the third KRA no influence, which is
 * what somebody who typed those three numbers asked for.
 */
export function meanRating(kras: ReadonlyArray<WeightedKraRating>): number | null {
  if (kras.length === 0) return null;
  const totalWeight = kras.reduce((sum, k) => sum + k.weightage, 0);
  if (totalWeight <= 0) {
    return roundRating(kras.reduce((sum, k) => sum + k.rating, 0) / kras.length);
  }
  return roundRating(kras.reduce((sum, k) => sum + k.weightage * k.rating, 0) / totalWeight);
}

export interface WeightedGoalRating {
  weightage: number;
  rating: number;
}

/**
 * Weightage-weighted mean across organizational goals.
 *
 * Callers must pass only fully-rated goals; `findUnratedGoals` exists so a
 * partially-reviewed assessment is rejected before it reaches here rather than
 * silently producing a rating computed from half the goals.
 */
export function weightedOverall(goals: ReadonlyArray<WeightedGoalRating>): number | null {
  const totalWeight = goals.reduce((sum, g) => sum + g.weightage, 0);
  if (totalWeight <= 0) return null;
  const weighted = goals.reduce((sum, g) => sum + g.weightage * g.rating, 0);
  return roundRating(weighted / totalWeight);
}

/** Indexes of goals with no rating yet - blocks stage submission. */
export function findUnratedGoals(
  goals: ReadonlyArray<{ rating: number | null }>,
): number[] {
  return goals.reduce<number[]>((acc, goal, index) => {
    if (goal.rating === null) acc.push(index);
    return acc;
  }, []);
}

export interface KraAnswer {
  kraId: string;
  rating?: number | null;
  comment?: string | null;
}

/**
 * A stage's answer to a KRA is a band AND the reason for it. Both are required to
 * submit; neither is required to save.
 *
 * Two lists rather than a boolean, because "you have not picked a band" and "you have
 * not written why" are different things to go and finish, and the refusal has to name
 * which. Whitespace is not a comment - a single space counts as missing, so trimming
 * on the client cannot buy anybody past the rule.
 *
 * Only submission calls this. A row with a comment and no band is still a note in
 * progress all quarter, which is the whole point of a nullable `KraRating.rating`.
 */
export function incompleteKraAnswers(
  kraIds: ReadonlyArray<string>,
  answers: ReadonlyArray<KraAnswer>,
): { unrated: string[]; uncommented: string[] } {
  const byKra = new Map(answers.map((a) => [a.kraId, a]));
  return {
    unrated: kraIds.filter((id) => byKra.get(id)?.rating == null),
    uncommented: kraIds.filter((id) => !byKra.get(id)?.comment?.trim()),
  };
}

export interface RatingSummary {
  overall: number | null;
  grade: Grade | null;
  label: string;
}

export function summariseRating(goals: ReadonlyArray<WeightedGoalRating>): RatingSummary {
  const overall = weightedOverall(goals);
  if (overall === null) return { overall: null, grade: null, label: 'Not rated' };
  const grade = toGrade(overall);
  return { overall, grade, label: gradeLabel(grade) };
}
